import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {convertRESTToWarehouseParameters} from 'insights/parameters/data/ParameterRESTConverters';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import {runUntrustedSQLQuery} from 'insights/warehouse/AnalyticsWarehouseDAO';
import {RunQueryOutput} from 'insights/warehouse/AnalyticsWarehouseTypes';
import convertRunQueryOutputToLegacyOutput from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import RunQueryOutputTable from 'insights/warehouse/RunQueryOutputTable';
import LLMFeedbackModal from 'llm/feedback/LLMFeedbackModal';
import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoDiffEditor from 'llm/monaco/MonacoDiffEditor';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Pill, {PillSize, PillStyle} from 'ui-library/buttons/Pill';
import Checkbox from 'ui-library/Checkbox';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {isServerRequestError} from 'util/Net';

import tryFormatSQLQuery from '../block-editor/formatSQL';
import {inferSQL, InferSQLResponse} from './SQLAssistantDAO';
import ValidationDot, {ValidationState} from './ValidationDot';

const BORDER_OFFSET = 2;
const MIN_EDITOR_HEIGHT = 218;
const MAX_EDITOR_HEIGHT = 600;
const PREVIEW_ROWS = 100;

export interface SQLAssistantModalProps {
  draftSql: string;
  parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
  onAccept: (sql: string, newParameterConfigs: Array<AnalyticsParameterConfigSpecRequest>) => void;
  onRequestClose: () => void;
}

const EXAMPLE_SQL = `SELECT --! email and handle of users who have created at least 10 entities
FROM benchling.registry_entity
ORDER BY --! number created from most to least`;

const LOADING_BLURBS = [
  'Submitted your query to the AI assistant…',
  'Analyzing your query…',
  'Extracting relevant tables…',
  'Loading warehouse schemas…',
  'Considering potential table joins…',
  'Picking most likely tables…',
  'Gathering sample data from likely tables…',
  'Coming up with a plan…',
  'Computing a SQL query suggestion…',
  'Validating SQL suggestion…',
  'Writing up a response…',
  'Almost there…',
  'Thanks for your patience, depending on your warehouse size and query complexity this can take a bit…',
];

const MEDIAN_LOADING_TIME_MS = 40000; // 40 seconds, adjust over time as latency improvements are made

function commentedError(err: string): string {
  return err
    .split('\n')
    .map((line) => `--! ${line}`)
    .join('\n');
}

export default function SQLAssistantModal(props: SQLAssistantModalProps): JSX.Element {
  const originalHasComments = props.draftSql.includes('--!');

  const [loadingInference, setLoadingInference] = useState(false);
  const [inferenceResult, setInferenceResult] = useState<InferSQLResponse | null>(null);
  const [suggestedSQL, setSuggestedSQL] = useState(!originalHasComments ? props.draftSql : '');

  const [inferrenceError, setInferrenceError] = useState<JSX.Element | string | null>(null);

  const [loadingDataset, setLoadingDataset] = useState(false);
  const [runQueryOutput, setRunQueryOutput] = useState<RunQueryOutput | null>(null);

  const [blurbIndex, setBlurbIndex] = useState(0);

  const [usefulFeedbackState, setUsefulFeedbackState] = useState<boolean | null>(null);

  const [diffEditorHeight, setDiffEditorHeight] = useState<number>(MIN_EDITOR_HEIGHT);
  const [shouldHighlightDiff, setShouldHighlightDiff] = useState(true);

  const runInference = async (): Promise<void> => {
    trackEvent(INSIGHTS_ANALYTICS_ACTIONS.REQUESTED_SQL_AI_ASSISTANT_SUGGESTION, {
      isRetry: inferenceResult != null,
    });
    if (inferrenceError) {
      setInferrenceError(null);
    }
    setBlurbIndex(0);
    setLoadingInference(true);
    const int = setInterval(
      () => {
        setBlurbIndex((prev) => Math.min(prev + 1, LOADING_BLURBS.length - 1));
      },
      Math.floor(MEDIAN_LOADING_TIME_MS / LOADING_BLURBS.length)
    );

    let inferenceResponse: InferSQLResponse;
    try {
      inferenceResponse = await inferSQL(
        suggestedSQL || props.draftSql,
        props.parameterConfigs,
        props.parameterValues
      );
    } catch (err) {
      clearInterval(int);
      setLoadingInference(false);
      setInferrenceError(
        notifications.getFriendlyErrorAsElement(err, '') ??
          'Sorry, an unexpected error occurred. Please try again or reach out to support@benchling.com if the issue persists.'
      );
      if (suggestedSQL === '') {
        // to allow immediate retrying, populate the suggestion with the original
        setSuggestedSQL(props.draftSql);
      }
      if (isServerRequestError(err)) {
        return;
      }
      throw err;
    }

    clearInterval(int);
    setInferenceResult(inferenceResponse);
    setLoadingInference(false);
    setUsefulFeedbackState(null);
    setSuggestedSQL(
      inferenceResponse.isValid
        ? tryFormatSQLQuery(inferenceResponse.suggestedSQL)
        : `${inferenceResponse.suggestedSQL.trim()}\n\n${commentedError(inferenceResponse.sqlError)}`
    );

    if (inferenceResponse.isValid) {
      // run the query to get the resulting dataset
      setLoadingDataset(true);
      const runQueryResponse = await runUntrustedSQLQuery({
        parameterizedSQLQuery: `WITH dataset AS (${inferenceResponse.suggestedSQL}\n) SELECT * FROM dataset LIMIT ${PREVIEW_ROWS}`,
        parameters: convertRESTToWarehouseParameters(props.parameterConfigs, props.parameterValues).concat(
          inferenceResponse.parameters ?? []
        ),
      });
      setLoadingDataset(false);
      setRunQueryOutput(runQueryResponse);
    }
  };

  useEffect(() => {
    if (originalHasComments) {
      runInference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFeedbackModal = (): void => {
    runModal(LLMFeedbackModal, {
      title: 'SQL assistant feedback',
      optionalContextItems: [
        {name: 'Original SQL', value: props.draftSql},
        {name: 'Suggested SQL', value: suggestedSQL},
      ],
      requiredContextItems: [
        {
          name: 'Useful',
          value: usefulFeedbackState ? '👍' : '👎',
        },
        {
          name: 'Trace ID',
          value: inferenceResult!.traceId,
        },
      ],
    });
  };

  return (
    <Modal
      title={
        <div className={css(styles.modalTitleContainer)}>
          <h4 className={css(styles.modalTitle)}>Insights SQL assistant</h4>
          <Pill size={PillSize.MINI} style={PillStyle.PRIMARY} icon="flask" text="BETA" />
        </div>
      }
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        trackEvent(INSIGHTS_ANALYTICS_ACTIONS.ACCEPTED_SQL_AI_ASSISTANT_SUGGESTION);
        props.onAccept(
          suggestedSQL,
          (inferenceResult?.parameters ?? []).map((p) => p.config)
        );
        props.onRequestClose();
      }}
      fullWidth={true}
      buttons={[
        {
          text: 'Retry suggestion',
          submit: false,
          disabled: !suggestedSQL || !suggestedSQL.includes('--!') || loadingInference,
          onClick: runInference,
        },
        {
          text: 'Accept SQL',
          submit: true,
          disabled: !suggestedSQL || loadingInference,
        },
      ]}
      closeableOnBackgroundClick={false}
      closeableOnEscapeKey={false}
      footer={
        <div className={css(styles.footerDisclaimer)}>
          <span className="glyphicon glyphicon-info-sign u-no-text-decoration" />
          <div>
            <p className={css(styles.disclaimerLine)}>This feature uses AI and may make mistakes.</p>
            <p className={css(styles.disclaimerLine)}>
              Always verify the results.{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/30901318550157-Insights-SQL-assistant"
                target="_blank"
                rel="noreferrer"
              >
                Learn more.
              </a>
            </p>
          </div>
        </div>
      }
    >
      <div className={css(styles.paddedGrid)}>
        {/* Tutorial / explanation state */}
        {!originalHasComments && !loadingInference && inferenceResult == null && (
          <>
            <p className={css(styles.usageExplanation)}>
              The SQL assistant works by analyzing your query and attempting to help with any comments using
              the <code>--!</code> syntax. The more information you provide the assistant the better it can
              help.
            </p>
            <p className={css(styles.usageExplanation)}>For example:</p>
            <MonacoEditor
              className={css(styles.exampleEditor)}
              height="56px"
              language="pgsql"
              value={EXAMPLE_SQL}
              width="100%"
              options={{
                lineNumbersMinChars: 3,
                minimap: {
                  enabled: false,
                },
                readOnly: true,
                renderLineHighlightOnlyWhenFocus: true,
              }}
              theme="vs"
            />
            <p>
              You can also leave comments before or after your SQL to give the assistant more information. For
              example you can note other tables you think you might need or to explain issues you are running
              into.
            </p>
          </>
        )}
        {/* Error state */}
        {inferrenceError && (
          <div>
            <p className={css(styles.errorMessage)}>{inferrenceError}</p>
          </div>
        )}
        {/* Loading state */}
        {loadingInference && <p className={css(styles.loadingBlurb)}>{LOADING_BLURBS[blurbIndex]}</p>}
        {!loadingInference && inferenceResult?.approach && (
          <LLMMarkdownSection markdown={inferenceResult.approach.trim()} />
        )}

        <div className={css(styles.split)}>
          <h4 className={css(styles.sectionHeader)}>Original SQL</h4>
          <div className={css(styles.flexSpaceBetween)}>
            <div className={css(styles.flex)}>
              <h4 className={css(styles.sectionHeader)}>Suggested SQL</h4>
              {suggestedSQL && inferenceResult != null ? (
                <Tooltip
                  text={
                    inferenceResult.isValid
                      ? 'Suggested SQL is valid'
                      : `Suggested SQL failed validation:\n\n${inferenceResult.sqlError ?? ''}`
                  }
                >
                  <ValidationDot
                    validationState={
                      (inferenceResult.isValid ?? false) ? ValidationState.VALID : ValidationState.INVALID
                    }
                  />
                </Tooltip>
              ) : (
                <ValidationDot validationState={ValidationState.NONE} />
              )}
            </div>

            <label className={css(styles.highlightToggle)}>
              <Checkbox
                checked={shouldHighlightDiff}
                onChange={() => {
                  setShouldHighlightDiff(!shouldHighlightDiff);
                }}
              />
              Highlight differences
            </label>
          </div>
        </div>

        <MonacoDiffEditor
          original={props.draftSql}
          value={suggestedSQL}
          language="pgsql"
          width="100%"
          height={diffEditorHeight}
          options={{
            lineDecorationsWidth: 8,
            lineNumbersMinChars: 3,
            originalEditable: false,
            readOnly: loadingInference,
            renderGutterMenu: false,
            renderIndicators: false,
            renderLineHighlightOnlyWhenFocus: true,
            renderMarginRevertIcon: false,
            renderOverviewRuler: false,
            scrollbar: {
              alwaysConsumeMouseWheel: false,
            },
            stickyScroll: {
              enabled: false,
            },
            useInlineViewWhenSpaceIsLimited: false,
          }}
          className={css(styles.diffEditor)}
          onChange={(value) => {
            setSuggestedSQL(value);
          }}
          onChangeContentSize={(e) => {
            if (e.contentHeightChanged) {
              setDiffEditorHeight(
                Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
              );
            }
          }}
          theme={shouldHighlightDiff ? 'vs' : 'noDiffHighlight'}
        />

        <div className={css(styles.split)}>
          <span className={css(styles.tip)}>
            Tip: Didn't get it first try? Edit the suggested SQL on the right, add new <code>--!</code>{' '}
            comments and click the retry button.
          </span>

          {isLLMFeedbackEnabled() && !loadingInference && inferenceResult != null && (
            <div className={css(styles.thumbsContainer)}>
              {usefulFeedbackState !== false && (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={() => {
                    trackEvent(INSIGHTS_ANALYTICS_ACTIONS.SUBMITTED_SQL_AI_ASSISTANT_SUGGESTION_USEFULNESS, {
                      traceId: inferenceResult.traceId,
                      useful_feedback_state: true,
                    });
                    setUsefulFeedbackState(true);
                  }}
                  disabled={usefulFeedbackState != null}
                  className={classNames(usefulFeedbackState === true && css(styles.selectedThumb))}
                >
                  <span className="fa fa-thumbs-up" />
                </Button>
              )}
              {usefulFeedbackState !== true && (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={() => {
                    trackEvent(INSIGHTS_ANALYTICS_ACTIONS.SUBMITTED_SQL_AI_ASSISTANT_SUGGESTION_USEFULNESS, {
                      traceId: inferenceResult.traceId,
                      useful_feedback_state: false,
                    });
                    setUsefulFeedbackState(false);
                  }}
                  disabled={usefulFeedbackState != null}
                  className={classNames(usefulFeedbackState === false && css(styles.selectedThumb))}
                >
                  <span className="fa fa-thumbs-down" />
                </Button>
              )}
              {usefulFeedbackState != null && (
                <a
                  role="button"
                  tabIndex={0}
                  onClick={openFeedbackModal}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      openFeedbackModal();
                    }
                  }}
                >
                  Tell us more
                </a>
              )}
            </div>
          )}
        </div>

        {inferenceResult?.isValid && (
          <>
            {loadingDataset && (
              <div className={css(styles.datasetLoadingContainer)}>
                <CenteredHorizontalThrobber />
                <span>Running SQL suggestion and fetching results preview…</span>
              </div>
            )}
            {!loadingDataset && runQueryOutput != null && (
              <>
                <div className={css(styles.datasetContainer)}>
                  <RunQueryOutputTable runQueryOutput={convertRunQueryOutputToLegacyOutput(runQueryOutput)} />
                </div>
                {runQueryOutput.dataFrame.rows.length === PREVIEW_ROWS && (
                  <p className={css(styles.truncationWarning)}>
                    Preview of suggested SQL results limited to {PREVIEW_ROWS} rows
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  exampleEditor: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    maxWidth: 625,
    minWidth: 360,
  },
  diffEditor: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    margin: '8px 0',
  },
  flexSpaceBetween: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  loadingEditor: {
    backgroundColor: Colors.GRAY_LIGHT,
    opacity: 0.65,
  },
  flex: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  footerDisclaimer: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    textAlign: 'left',
  },
  datasetContainer: {
    maxHeight: 380,
    overflowX: 'auto',
  },
  datasetLoadingContainer: {
    display: 'grid',
    justifyContent: 'center',
  },
  disclaimerLine: {
    marginBottom: 0,
  },
  loadingBlurb: {
    fontSize: 15,
    fontStyle: 'italic',
    fontWeight: 500,
    textAlign: 'center',
  },
  modalTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  modalTitle: {
    margin: 0,
  },
  usageExplanation: {
    margin: 0,
  },
  paddedGrid: {
    display: 'grid',
    fontSize: 13,
    gap: 8,
  },
  sectionHeader: {
    margin: 0,
  },
  split: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  selectedThumb: {
    color: Colors.NEAR_BLACK,
  },
  thumbsContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  tip: {
    display: 'block',
    fontStyle: 'italic',
  },
  truncationWarning: {
    fontStyle: 'italic',
    textAlign: 'end',
  },
  highlightToggle: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    marginBottom: 0,
  },
  errorMessage: {
    backgroundColor: Colors.RED_LIGHTEST,
    border: `1px solid ${Colors.RED_DARK}`,
    borderRadius: 4,
    color: Colors.RED_DARK,
    padding: '4px 8px',
    textAlign: 'center',
  },
});
